import { Grid, Theme, useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import { get as _get } from 'lodash';
import { useMemo } from 'react';

import { FnolQuestionTypes } from '../../../model/create-draft-claim-request';
import { getVehicleDamageAnswer } from '../../../services/SetupFnol.service';
import { formatDateAndTime, getPrettyPhoneNumber } from '../../../utils/Utilities';
import ReviewFnolSummary from './ReviewFnolSummary';

export default function ReviewFnol(props: any) {
  const theme: Theme = useTheme();

  const isAuto = props.isAuto;
  const isThirdParty = props.isThirdParty;

  const summaryInfo = useMemo(() => {
    const localSummaryInfo = [];

    if (isThirdParty) {
      const claimantQuestion = _get(props, 'reviewSheet', []).find(
        (question: any) => _get(question, 'questionType', '') === FnolQuestionTypes.CLAIMANT_CONTACT_TYPE
      );
      const claimantForm = claimantQuestion.answerData;
      if (claimantForm) {
        if (claimantForm.vehicleDetail) {
          const riskRow = {
            rowName: 'Vehicle',
            rowValue:
              _get(claimantForm, 'claimantContactForm.vehicleDetail.vehTypeYear', '') +
              ' ' +
              _get(claimantForm, 'claimantContactForm.vehicleDetail.vehTypeMake', '') +
              ' ' +
              _get(claimantForm, 'claimantContactForm.vehicleDetail.vehTypeModel', '') +
              ' ',
          };
          localSummaryInfo.push(riskRow);
        }
        const reporterRow = {
          rowName: 'Reported By',
          rowValue:
            _get(claimantForm, 'claimantContactForm.claimContact.contact.firstName', '') +
            ' ' +
            _get(claimantForm, 'claimantContactForm.claimContact.contact.lastName', ''),
          mask: true,
        };
        localSummaryInfo.push(reporterRow);
        const dateTimeRow = {
          rowName: 'Date and Time',
          rowValue: formatDateAndTime(_get(props, 'claimInfo.dateLocationInfo.lossDate', '')),
        };
        localSummaryInfo.push(dateTimeRow);

        const locationRow = {
          rowName: 'Location',
          rowValue:
            _get(props, 'claimInfo.dateLocationInfo.city', '') +
            ', ' +
            _get(props, 'claimInfo.dateLocationInfo.state', ''),
        };
        localSummaryInfo.push(locationRow);

        const contactInfoQuestionId = _get(claimantQuestion, 'questionId');
        const phoneNumber =
          _get(claimantForm, 'claimantContactForm.claimContact.contact.newPrimaryPhoneEmail.area', '') +
          _get(claimantForm, 'claimantContactForm.claimContact.contact.newPrimaryPhoneEmail.number', '');
        const phoneRow = {
          rowName: 'Phone Number',
          rowValue: getPrettyPhoneNumber(phoneNumber),
          mask: true,
          showEdit: !!contactInfoQuestionId,
          questionId: contactInfoQuestionId,
        };
        localSummaryInfo.push(phoneRow);

        const emailRow = {
          rowName: 'Email Address',
          rowValue: _get(
            claimantForm,
            'claimantContactForm.claimContact.contact.newPrimaryPhoneEmail.emailAddress',
            ''
          ),
          mask: true,
          showEdit: !!contactInfoQuestionId,
          questionId: contactInfoQuestionId,
        };
        localSummaryInfo.push(emailRow);
      }
    } else if (isAuto) {
      const vehicleInfo = _get(props, 'claimInfo.draftPayload.vehicleDetails[0]');

      const riskRow = {
        rowName: 'Vehicle',
        rowValue: `${_get(vehicleInfo, 'vehTypeYear')} ${_get(vehicleInfo, 'vehTypeMake')} ${_get(
          vehicleInfo,
          'vehTypeModel'
        )}`,
      };

      localSummaryInfo.push(riskRow);
      if (
        _get(props, 'claimInfo.reporterInfo.firstName', '-1') !== _get(props, 'claimInfo.driverInfo.firstName', '-2') ||
        _get(props, 'claimInfo.reporterInfo.lastName', '-1') !== _get(props, 'claimInfo.driverInfo.lastName', '-2')
      ) {
        const reporterRow = {
          rowName: 'Reported By',
          rowValue:
            _get(props, 'claimInfo.reporterInfo.firstName', '') +
            ' ' +
            _get(props, 'claimInfo.reporterInfo.lastName', ''),
          mask: true,
        };
        localSummaryInfo.push(reporterRow);
      }
      const driverRow = {
        rowName: 'Driver',
        rowValue:
          _get(props, 'claimInfo.driverInfo.firstName', '') + ' ' + _get(props, 'claimInfo.driverInfo.lastName', ''),
        mask: true,
      };
      localSummaryInfo.push(driverRow);

      const dateTimeRow = {
        rowName: 'Date and Time',
        rowValue: formatDateAndTime(_get(props, 'claimInfo.dateLocationInfo.lossDate', '')),
      };
      localSummaryInfo.push(dateTimeRow);

      const locationRow = {
        rowName: 'Location',
        rowValue:
          _get(props, 'claimInfo.dateLocationInfo.city', '') +
          ', ' +
          _get(props, 'claimInfo.dateLocationInfo.state', ''),
      };

      localSummaryInfo.push(locationRow);
      const contactInfoQuestion = _get(props, 'reviewSheet', []).find(
        (question: any) => _get(question, 'questionType', '') === FnolQuestionTypes.INSURED_CONTACT_TYPE
      );
      const contactInfoQuestionId = _get(contactInfoQuestion, 'questionId');

      const phoneNumber =
        _get(contactInfoQuestion, 'answerData.areaCode', '') + _get(contactInfoQuestion, 'answerData.phoneNumber', '');

      const phoneRow = {
        rowName: 'Phone Number',
        rowValue: getPrettyPhoneNumber(phoneNumber),
        mask: true,
        showEdit: !!contactInfoQuestionId,
        questionId: contactInfoQuestionId,
      };
      localSummaryInfo.push(phoneRow);

      const emailRow = {
        rowName: 'Email Address',
        rowValue: _get(contactInfoQuestion, 'answerData.email', ''),
        mask: true,
        showEdit: !!contactInfoQuestionId,
        questionId: contactInfoQuestionId,
      };
      localSummaryInfo.push(emailRow);
    } else {
      const riskRow = {
        rowName: 'Property',
        rowValue: _get(props, 'claimInfo.riskInfo.description', ''),
      };
      localSummaryInfo.push(riskRow);

      const reporterRow = {
        rowName: 'Reported By',
        rowValue:
          _get(props, 'claimInfo.reporterInfo.firstName', '') +
          ' ' +
          _get(props, 'claimInfo.reporterInfo.lastName', ''),
        mask: true,
      };
      localSummaryInfo.push(reporterRow);

      const dateTimeRow = {
        rowName: 'Date and Time',
        rowValue: formatDateAndTime(_get(props, 'claimInfo.dateLocationInfo.lossDate', '')),
      };
      localSummaryInfo.push(dateTimeRow);
      const contactInfoQuestion = _get(props, 'reviewSheet', []).find(
        (question: any) => _get(question, 'questionType', '') === FnolQuestionTypes.INSURED_CONTACT_TYPE
      );
      const contactInfoQuestionId = _get(contactInfoQuestion, 'questionId');
      const phoneNumber =
        _get(contactInfoQuestion, 'answerData.areaCode', '') + _get(contactInfoQuestion, 'answerData.phoneNumber', '');
      const phoneRow = {
        rowName: 'Phone Number',
        rowValue: getPrettyPhoneNumber(phoneNumber),
        mask: true,
        showEdit: !!contactInfoQuestionId,
        questionId: contactInfoQuestionId,
      };
      localSummaryInfo.push(phoneRow);

      const emailRow = {
        rowName: 'Email Address',
        rowValue: _get(contactInfoQuestion, 'answerData.email', ''),
        mask: true,
        showEdit: !!contactInfoQuestionId,
        questionId: contactInfoQuestionId,
      };
      localSummaryInfo.push(emailRow);
    }

    return localSummaryInfo;
  }, [isThirdParty, isAuto, props.claimInfo, props.reviewSheet]);

  const [incidentInfo, thirdPartyInfo] = useMemo(() => {
    const localIncidentInfo: any[] = [];
    const localThirdPartyInfo: any[] = [];

    _get(props, 'reviewSheet', []).forEach((question: any) => {
      if (
        _get(question, 'reviewAnswerDisplay', 'N/A') !== 'N/A' ||
        _get(question, 'questionType', '') === FnolQuestionTypes.VEHICLE_DAMAGE_PICKER ||
        _get(question, 'questionType', '') === FnolQuestionTypes.THIRD_PARTY_CONTACT_FORM
      ) {
        switch (_get(question, 'questionType', '')) {
          case FnolQuestionTypes.MULTI_SELECT: {
            const answers = _get(question, 'reviewAnswerDisplay', '').split(',');
            answers.forEach((answer: any) => {
              localIncidentInfo.push({
                rowName: '',
                rowValue: answer,
                showEdit: true,
                questionId: _get(question, 'questionId', ''),
              });
            });
            break;
          }
          case FnolQuestionTypes.VEHICLE_DAMAGE_PICKER:
            localIncidentInfo.push({
              rowName: _get(question, 'reviewQuestionDisplay', ''),
              rowValue: getVehicleDamageAnswer(_get(question, 'answerData.damageType', '')),
              showEdit: true,
              questionId: _get(question, 'questionId', ''),
            });
            break;
          case FnolQuestionTypes.SINGLE_SELECT:
          case FnolQuestionTypes.FREE_FORM:
            localIncidentInfo.push({
              rowName: _get(question, 'reviewQuestionDisplay', ''),
              rowValue: _get(question, 'reviewAnswerDisplay', ''),
              mask: _get(question, 'questionType', '') === FnolQuestionTypes.FREE_FORM,
              showEdit: true,
              questionId: _get(question, 'questionId', ''),
            });
            break;
          case FnolQuestionTypes.THIRD_PARTY_CONTACT_FORM: {
            const contacts = _get(question, 'answerData.thirdPartyContactForm', []);
            contacts.forEach((contact: any, index: number) => {
              localThirdPartyInfo.push({
                rowName: 'Person ' + (index + 1),
                rowValue:
                  _get(contact, 'claimContact.contact.firstName', '') +
                  ' ' +
                  _get(contact, 'claimContact.contact.lastName', ''),
                mask: true,
                showEdit: true,
                questionId: _get(question, 'questionId', ''),
              });
            });
            break;
          }

          default:
            break;
        }
      }
    });

    return [localIncidentInfo, localThirdPartyInfo];
  }, [props.reviewSheet]);

  function handleEdit(row: any) {
    props.handleEdit(row);
  }

  return (
    <Grid
      container
      sx={{
        width: '100%',
      }}
    >
      <Grid item xs={12}>
        <Typography
          sx={{
            ...theme.custom.styles.titleType4,
          }}
        >
          Review Your Claim
        </Typography>

        <Typography sx={{ marginTop: '1em' }}>
          Here's a quick glance at the details you entered. Does everything look correct? If needed, you can make
          changes below before submitting.
        </Typography>
      </Grid>

      {summaryInfo && (
        <>
          <Grid item sx={{ marginTop: '2em' }} xs={12}>
            <Typography
              sx={{
                ...theme.custom.styles.titleType4,
              }}
            >
              Basic Claim Information
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <ReviewFnolSummary handleEdit={handleEdit} info={summaryInfo}></ReviewFnolSummary>
          </Grid>
        </>
      )}

      {incidentInfo && incidentInfo.length > 0 && (
        <>
          <Grid item sx={{ marginTop: '2em' }} xs={12}>
            <Typography
              sx={{
                ...theme.custom.styles.titleType4,
              }}
            >
              Incident Details
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <ReviewFnolSummary handleEdit={handleEdit} info={incidentInfo}></ReviewFnolSummary>
          </Grid>
        </>
      )}

      {thirdPartyInfo && thirdPartyInfo.length > 0 && (
        <>
          <Grid item sx={{ marginTop: '2em' }} xs={12}>
            <Typography
              sx={{
                ...theme.custom.styles.titleType4,
              }}
            >
              Contact Information for Others Involved
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <ReviewFnolSummary handleEdit={handleEdit} info={thirdPartyInfo}></ReviewFnolSummary>
          </Grid>
        </>
      )}
    </Grid>
  );
}
